import { useMutation, useQuery, useQueryClient } from 'react-query';

import { http, type JsonData, camelCaseToUnderscore, underscoreToCamelCase } from '@/api/base/http';
import type { Tag, TagWithCount } from '@/api/types/tag';

export const useTagQuery = (args: Record<string, unknown> = {}) => {
    return useQuery<TagWithCount[], Error>({
        queryKey: ['tags'],
        queryFn: () => http.get<JsonData>('/tags').then((d) => underscoreToCamelCase(d)),
        ...args,
    });
};

export const useDeleteTag = (args: Record<string, unknown> = {}) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: number) => http.delete<void>(`/tags/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries('tags');
        },
        ...args,
    });
};

export const useCreateTag = (args: Record<string, unknown> = {}) => {
    const queryClient = useQueryClient();
    if ('onSuccess' in args) {
        const onSuccess = args.onSuccess as (data: Tag) => void;
        args.onSuccess = (data: Tag) => {
            queryClient.invalidateQueries('tags');
            onSuccess(data);
        };
    } else {
        args.onSuccess = (data: Tag) => {
            queryClient.invalidateQueries('tags');
        };
    }
    return useMutation({
        mutationFn: (name: string) =>
            http
                .post<Tag>('/tags', camelCaseToUnderscore({ name }) as Record<string, unknown>)
                .then((data) => underscoreToCamelCase(data)),
        ...args,
    });
};
